/**
 * User Store (Zustand)
 *
 * Manages authentication, user list, and session lifecycle.
 * Provides user info needed by the comment panel (activeUser, users, switchUser).
 *
 * NOTE: isLoading / auth gates removed by design — the module does not block
 * rendering on authentication. Call bootstrap() at app startup to restore
 * session or auto-login.
 */

import {create} from 'zustand';

import httpClient, {
  TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  setAuthFailedCallback
} from '../../services/modules/ReportBuilder/api/httpClient';
import getReportBuilderUsers from '../../services/modules/User/getReportBuilderUsers';
import useUserStore from '../userStore/userStore';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface UserState {
  activeUser: any;
  users: any[];
  authError: string | null;
}

export interface UserActions {
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  switchUser: (userId: string) => void;
  fetchUsers: () => Promise<any[]>;
  bootstrap: () => Promise<void>;
  handleSessionExpired: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const decodeToken = (
  token: string
): {id: string; email: string; exp: number} | null => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));

    return payload;
  } catch {
    return null;
  }
};

const isTokenValid = (token: string): boolean => {
  const payload = decodeToken(token);
  if (!payload) return false;

  return payload.exp * 1000 > Date.now() + 10_000;
};

const USER_COLORS = [
  '#D32F2F',
  '#1976D2',
  '#388E3C',
  '#FBC02D',
  '#7B1FA2',
  '#E64A19',
  '#0097A7',
  '#5D4037',
  '#455A64',
  '#C2185B'
];

const getDynamicColor = (seed: string): string => {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }

  return USER_COLORS[Math.abs(hash) % USER_COLORS.length];
};

const getInitials = (firstName: string, lastName: string, name: string): string => {
  const first = firstName?.trim()?.[0] ?? '';
  const last = lastName?.trim()?.[0] ?? '';
  if (first || last) {
    return `${first}${last}`.toUpperCase();
  }

  return (name?.trim()?.[0] ?? 'U').toUpperCase();
};

const normalizeUser = (rawUser: any) => {
  const id = rawUser?._id ?? rawUser?.id ?? '';
  const firstName = rawUser?.firstName ?? rawUser?.userFirstName ?? '';
  const lastName = rawUser?.lastName ?? rawUser?.userLastName ?? '';
  const nameFromParts = `${firstName} ${lastName}`.trim();
  const name = rawUser?.name ?? nameFromParts ?? '';
  const stableSeed = id || rawUser?.email || name || 'unknown';

  return {
    id,
    name: name || 'Unknown User',
    firstName,
    lastName,
    email: rawUser?.email ?? '',
    avatar: rawUser?.avatar ?? getInitials(firstName, lastName, name),
    color: rawUser?.color ?? getDynamicColor(stableSeed)
  };
};

// ─── Store ───────────────────────────────────────────────────────────────────

export const useReportUserStore = create<UserState & UserActions>()(
  (set, get) => ({
    activeUser: null,
    users: [],
    authError: null,

    fetchUsers: async () => {
      try {
        const currentUser = useUserStore.getState().userDetails ?? {};
        const currentUserId = currentUser?._id ?? currentUser?.id ?? '';
        const rawUsers = await getReportBuilderUsers('');
        const normalizedUsers = (Array.isArray(rawUsers) ? rawUsers : [])
          .map(normalizeUser)
          .filter((u) => u.id);
        const uniqueUsers = Array.from(
          new Map(normalizedUsers.map((u) => [u.id, u])).values()
        );
        const fallbackCurrentUser = currentUserId
          ? normalizeUser({
              ...currentUser,
              _id: currentUserId
            })
          : null;
        const activeUser =
          uniqueUsers.find((u) => u.id === currentUserId) ??
          fallbackCurrentUser ??
          uniqueUsers[0] ??
          null;
        const usersWithFallback =
          fallbackCurrentUser &&
          !uniqueUsers.some((u) => u.id === fallbackCurrentUser.id)
            ? [fallbackCurrentUser, ...uniqueUsers]
            : uniqueUsers;

        set({users: usersWithFallback, activeUser});

        return usersWithFallback;
      } catch {
        return [];
      }
    },

    login: async (email, password) => {
      set({authError: null});
      try {
        const res = await httpClient.post<{
          accessToken: string;
          refreshToken: string;
          user: any;
        }>('/api/auth/login', {email, password});

        localStorage.setItem(TOKEN_KEY, res.data.accessToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, res.data.refreshToken);

        const allUsers = await get().fetchUsers();
        const me =
          allUsers.find((u: any) => u.id === res.data.user.id) ?? res.data.user;
        set({activeUser: me});
      } catch (err: any) {
        const msg =
          err?.response?.data?.error ?? 'Login failed. Please try again.';
        set({authError: msg});
        throw new Error(msg);
      }
    },

    logout: () => {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      set({activeUser: null, users: [], authError: null});
    },

    switchUser: (userId) => {
      const user = get().users.find((u) => u.id === userId);
      if (user) set({activeUser: user});
    },

    handleSessionExpired: () => {
      set({
        activeUser: null,
        users: [],
        authError: 'Your session has expired.'
      });
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);

      // Attempt auto-login again
      const email =
        process.env.REACT_APP_MOCK_USER_EMAIL ?? 'shrutijadhav4@email.com';
      const password = process.env.REACT_APP_MOCK_USER_PASSWORD ?? 'password';
      get().login(email, password).catch(console.error);
    },

    bootstrap: async () => {
      const token = localStorage.getItem(TOKEN_KEY);

      if (token && isTokenValid(token)) {
        try {
          const allUsers = await get().fetchUsers();
          const payload = decodeToken(token);
          const me =
            allUsers.find((u: any) => u.id === payload?.id) ??
            allUsers[0] ??
            null;
          set({activeUser: me});

          return;
        } catch {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(REFRESH_TOKEN_KEY);
        }
      }

      // Fall back to auto-login
      const email =
        process.env.REACT_APP_MOCK_USER_EMAIL ?? 'shrutijadhav4@email.com';
      const password = process.env.REACT_APP_MOCK_USER_PASSWORD ?? 'password';
      try {
        await get().login(email, password);
      } catch (err) {
        console.error('Auto-login failed:', err);
      }
    }
  })
);

// ─── Wire up httpClient session-expired event ────────────────────────────────
setAuthFailedCallback(() => {
  useReportUserStore.getState().handleSessionExpired();
});
